import {
  Criterion,
  CriterionListItem,
  FieldType,
  ListValueType,
  LogicJumpActionType,
  ParticipantField,
  ParticipantFieldType,
  SelectChoiceField,
  SelectionBehavior,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const GDPRConsentRef = 'GDPRConsent';

/**
 * Whether the offer lets the field be targeted, by its name or one of its aliases. An
 * offer without a list of criteria allows them all.
 */
export const isFieldAllowed = (
  allowedFields: string[] | undefined,
  field: ParticipantField,
): boolean => {
  if (!allowedFields || !allowedFields.length) {
    return true;
  }
  if (allowedFields.includes(field.name)) {
    return true;
  }
  if (!field.criterion || !field.criterion.aliases) {
    return false;
  }
  return field.criterion.aliases.some((alias) => allowedFields.includes(alias));
};

export const useGetCriterionValueText = () => {
  const { t } = useTranslation();
  return useCallback(
    (value: CriterionListItem, field?: ParticipantField) => {
      if (typeof value === 'string' || typeof value === 'number') {
        const choices = field?.choices || [];

        if (!choices) {
          return String(value);
        }

        const found = choices.find(
          (choice) => String(choice.value) === String(value),
        );
        if (found) {
          return found.label;
        }
        return String(value);
      } else if ('location' in value) {
        return value.name;
      } else {
        const key = value.min ? (value.max ? 'min_max' : 'min') : 'max';
        if (field?.type === ParticipantFieldType.DATE) {
          return t(`criteria.ageRange.${key}`, value);
        }
        return t(`criteria.range.${key}`, value);
      }
    },
    [t],
  );
};

export const useGetParticpantFieldCriterionTextValue = () => {
  const getCriterionValueText = useGetCriterionValueText();
  const { t } = useTranslation();

  return useCallback(
    (criterion: Criterion, field: ParticipantField): string => {
      let allowTexts: string[] = [];
      if (criterion.allowList) {
        allowTexts = criterion.allowList.map((val) =>
          getCriterionValueText(val, field),
        );
      }

      let denyTexts: string[] = [];
      if (criterion.denyList) {
        denyTexts = criterion.denyList.map((val) =>
          getCriterionValueText(val, field),
        );
      }

      if (denyTexts.length) {
        let result = '';
        if (allowTexts.length) {
          result += allowTexts.join(', ');
        } else {
          result += t('criteria.lists.everything');
        }
        result += '\n';
        result += t('criteria.lists.except', {
          values: denyTexts.join(', '),
        });
        return result;
      }

      if (!allowTexts.length) {
        return t('criteria.lists.everything');
      }

      return allowTexts.join(', ');
    },
    [t],
  );
};

export const useCreateGDPRConsentFieldAndCriterion = () => {
  const { t } = useTranslation();

  return useCallback(() => {
    const acceptRef = uuidv4();
    const refuseRef = uuidv4();
    const fieldId = uuidv4();

    const newConsentField = {
      id: fieldId,
      title: t('criteria.gdpr.question'),
      type: FieldType.SINGLE_CHOICE,
      ref: GDPRConsentRef,
      properties: {
        randomize: false,
        allowMultipleSelection: false,
        autocomplete: false,
        choices: [
          {
            ref: acceptRef,
            label: t('criteria.gdpr.accept'),
          },
          {
            ref: refuseRef,
            label: t('criteria.gdpr.decline'),
          },
        ],
      },
      validation: {
        required: true,
      },
      jumpActions: [
        {
          id: uuidv4(),
          fieldId: fieldId,
          type: LogicJumpActionType.END_FORM,
          conditionChoiceRefs: [refuseRef],
        },
      ],
    } as SelectChoiceField;

    const criterion = {
      id: uuidv4(),
      fieldRef: GDPRConsentRef,
      screenerCriterion: true,
      selectionBehavior: SelectionBehavior.OPTIONAL,
      allowList: [acceptRef, refuseRef],
      automaticlyAcceptedList: [acceptRef],
      listValueType: ListValueType.KEYWORD,
      denyList: [],
    };

    return { newConsentField, criterion };
  }, [t]);
};

export function stringityItem(item: CriterionListItem): string {
  if (typeof item !== 'object') {
    return String(item);
  }
  if ('location' in item) {
    if (!item.radius) {
      return `${item.location?.lat},${item.location?.lon}`;
    }
    return `${item.location?.lat},${item.location?.lon}-${item.radius}`;
  }

  if ('min' in item || 'max' in item) {
    return `${item.min ?? 0}-${item.max ?? 'Infinity'}`;
  }

  // Should not happen
  return JSON.stringify(item);
}
